<template>
    <b-modal size="lg" :visible="visible" no-close-on-backdrop no-close-on-esc centered>
        <div slot="modal-header" class="w-100">
            <h5>{{title}}<button type="button" class="close"
                                        @click.prevent="closeModal()">
                <span aria-hidden="true">&times;</span></button>
            </h5>
        </div>
        <b-form>
            <div class="container-fluid visit-charge-body">
                <div class="row justify-content-center m-2">
                    <div class="alert alert-danger col" role="alert" v-show="errorMessage">
                        <div>{{ errorMessage }}</div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-2"></div>
                    <div class="col-8">
                        <div class="row">
                            <div class="col-4 px-0 text-right">
                                <h6>Current Charges:</h6>
                            </div>
                            <div class="col px-2">
                                <div v-for="charge in visitCharges" :key="charge.visitChargeId" class="current-charge">
                                    {{ charge.description + ' - ' + charge.amount }}
                                    <font-awesome-icon @click="removeCharge(charge)" class="mx-1" :icon="['far', 'trash-alt']"/>
                                </div>
                                <div v-if="visitCharges.length === 0">
                                    No current charges.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <hr/>
                    </div>
                </div>
                <div class="row">
                    <div class="col-2">
                    </div>
                    <div class="col-8">
                      <b-form-group :label-cols="4"
                                    label="Visit Charge Type"
                                    :state="stateForField('type')"
                                    :invalid-feedback="errorForField('type')"
                                    label-class="text-right">
                        <b-dropdown menu-class="dropdown-container" :text="chargeTypeText" :variant="hasError('type') ? 'outline-danger' : 'outline-dark'">
                          <b-dropdown-item v-for="chargeType in chargeTypes" :key="chargeType.value"
                                           @click.prevent="setChargeType(chargeType)">{{ chargeType.text }}
                          </b-dropdown-item>
                        </b-dropdown>
                      </b-form-group>

                      <b-form-group id="amountFieldGroup"
                                      horizontal
                                      :label-cols="4"
                                      breakpoint="md"
                                      :state="stateForField('amount')"
                                      :invalid-feedback="errorForField('amount')"
                                      label="Amount"
                                      label-for="amountField"
                                      label-class="text-right">
                            <b-input-group prepend="$">
                                <b-form-input id="amountField" v-model="charge.amount"></b-form-input>
                            </b-input-group>
                        </b-form-group>
                        <b-form-group id="descriptionFieldGroup"
                                      horizontal
                                      :label-cols="4"
                                      breakpoint="md"
                                      :state="stateForField('description')"
                                      :invalid-feedback="errorForField('description')"
                                      label="Description"
                                      label-for="descriptionField"
                                      label-class="text-right">
                            <b-form-input id="descriptionField" v-model="charge.description"></b-form-input>
                            <div class="pt-1">
                                <font-awesome-icon size="sm" class="text-warning mr-2 align-baseline" icon="exclamation-triangle"/> <span class="text-muted align-bottom">This will be visible to the patient.</span>
                            </div>
                            <div class="pt-1">
                                <span :class="{ 'text-danger': charge.description.length > 25, 'text-muted': charge.description.length <= 25, 'align-bottom': true }">{{ charge.description.length }} of 25 max characters used</span>
                            </div>
                        </b-form-group>
                    </div>
                </div>
            </div>
        </b-form>
        <div slot="modal-footer" class="w-100">
            <button type="button" class="btn btn-orange float-right" @click.prevent="addCharge()"><font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner"  pulse/>Add Charge</button>
            <button type="button" class="btn btn-outline-orange mr-2 float-right" @click.prevent="closeModal()">Close</button>
        </div>
    </b-modal>
</template>

<script>
  import api from '../services/api'
  import ErrorsMixin from '../mixins/ErrorsMixin.js'

  export default {
    name: 'VisitChargesModal',

    mixins: [ErrorsMixin],

    props: {
      visible: {
        type: Boolean,
        required: true
      },

      visitId: {
        type: Number,
        required: true
      },

      title: {
        type: String,
        default: 'Visit Charges'
      }
    },

    data () {
      return {
        charge: {
          description: '',
          amount: '',
          type: 'Other'
        },
        visitCharges: [],
        isSaving: false,
        errorMessage: '',
        chargeTypes: [
          { value: 'CoPay', text: 'Copay' },
          { value: 'Other', text: 'Other' },
          { value: 'OutstandingBalance', text: 'Outstanding Balance' },
          { value: 'SelfPay', text: 'Self-Pay' }
        ],
        chargeTypeText: 'Select Visit Charge Type',
        changed: false
      }
    },

    watch: {
      visible (newValue, oldValue) {
        if (newValue) {
          this.clearData()
          if (this.visitId > 0) {
            this.loadCharges()
          }
        }
      }
    },

    methods: {
      clearData () {
        this.clearAllErrors()
        this.visitCharges = []
        this.charge = {
          description: '',
          amount: '',
          type: 'Other'
        }
        this.changed = false
        this.chargeTypeText = 'Select Visit Charge Type'
      },

      validateCharge () {
        this.clearAllErrors()
        const amountValue = parseFloat(this.charge.amount)
        if (!amountValue) {
          this.addErrorForField('amount', 'An amount is required.')
        }

        if (amountValue < 0) {
          this.addErrorForField('amount', 'Amount must be greater then zero.')
        }

        if (!this.charge.description) {
          this.addErrorForField('description', 'A description is required.')
        }

        if (this.charge.description.length > 25) {
          this.addErrorForField('description', 'The description must be less then 25 characters')
        }

        return !this.hasErrors()
      },

      loadCharges () {

        api.get('/Dashboard/Visit/' + this.visitId + '/Charges').then(response => {

          this.visitCharges = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.errorMessage = error.message
            this.$log.error('Error attempting to load visit charges: ' + error.message, error)
          }
        })
      },

      addCharge () {

        if (this.isSaving) {
          return
        }

        if (!this.validateCharge()) {
          return
        }

        this.isSaving = true
        this.errorMessage = ''
        api.post('/Dashboard/Visit/' + this.visitId + '/Charge', this.charge).then(response => {

          const visitCharge = response.data
          this.visitCharges.push(visitCharge)
          this.changed = true

          this.$emit('chargeAdded', visitCharge)

        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.errorMessage = error.message
            this.$log.error('Error attempting to add visit charge: ' + error.message, error)
          }
        }).then(() => {
          this.isSaving = false
        })
      },

      removeCharge (charge) {
        this.errorMessage = ''
        api.delete('/Dashboard/Visit/Charge/' + charge.visitChargeId).then(response => {
          this.loadCharges()
          this.changed = true
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.errorMessage = error.message
            this.$log.error('Error attempting to remove charge: ' + error.message, error)
          }
        })
      },

      setChargeType (chargeType) {
        this.charge.type = chargeType.value
        this.chargeTypeText = chargeType.text
      },

      closeModal () {
        this.$emit('closeModal', this.changed)
      }
    }
  }
</script>

<style scoped>
    .visit-charge-body {
        font-size: 16px;
    }

    .current-charge .fa-trash-can {
        cursor: pointer;
    }

    .current-charge .fa-trash-can:HOVER {
        color: rgb(255, 70, 0);
    }

</style>
